import { styled } from "@mui/material/styles";
import { ChevronRight, Search as SearchIcon } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCommandDropdown } from "../context";
import { SearchProps } from "../types";

// Styled input wrapper with breadcrumbs support
const SearchWrapper = styled("div")(
    () => `
    width: 100%;
    border: 1px solid;
    background-color: #fff;
    border-color: #E5E5E5;
    border-radius: 8px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: .15s;

    &:focus-within {
        border-color: #7556FA;
    }

    & input {
        background-color: transparent;
        height: 28px;
        box-sizing: border-box;
        width: 0;
        min-width: 30px;
        flex-grow: 1;
        border: 0;
        margin: 0;
        padding: 0;
        outline: 0;
        font-size: 14px;
    }

    & input::placeholder {
        color: #D9D6DA;
        font-weight: 400;
    }
`
);

const Breadcrumb = styled("button")(
    () => `
    display: flex;
    align-items: center;
    gap: 4px;
    color: #7556FA;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;
    flex-shrink: 0;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
`
);

const BreadcrumbSeparator = styled(ChevronRight)(
    () => `
    color: #D9D6DA;
    flex-shrink: 0;
`
);

export function Search({ autoFocus = true, className, render }: SearchProps) {
    const { t } = useTranslation("", { keyPrefix: "general.components.command" });

    const placeholderText = t("search.placeholder");

    const { searchValue, setSearchValue, navigationStack, popNavigation, isOpen, navigateToIndex } =
        useCommandDropdown();
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (autoFocus) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [autoFocus]);

    // Focus when modal opens (BaseModal keeps children mounted)
    useEffect(() => {
        if (autoFocus && isOpen) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [autoFocus, isOpen]);

    // Re-focus when navigation changes
    useEffect(() => {
        if (autoFocus) {
            inputRef.current?.focus();
        }
    }, [navigationStack.length, autoFocus]);

    const inputProps: React.InputHTMLAttributes<HTMLInputElement> = {
        type: "text",
        placeholder: placeholderText,
        value: searchValue,
        onChange: (e) => setSearchValue(e.target.value),
        className,
    };

    // Custom render function
    if (render) {
        return <>{render({ value: searchValue, onChange: setSearchValue, inputProps })}</>;
    }

    // Default render with breadcrumbs
    return (
        <SearchWrapper className={className}>
            <SearchIcon size={16} className="text-[#D9D6DA] flex-shrink-0" />

            {/* Breadcrumbs */}
            {navigationStack.map((nav, index) => (
                <React.Fragment key={nav.key}>
                    <Breadcrumb
                        type="button"
                        onClick={() => navigateToIndex(index)}
                        aria-label={`Go to ${nav.label}`}
                    >
                        {nav.label}
                    </Breadcrumb>
                    <BreadcrumbSeparator size={14} />
                </React.Fragment>
            ))}

            {/* Input */}
            <input
                ref={inputRef}
                type="text"
                placeholder={placeholderText}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                autoFocus={autoFocus}
                onKeyDown={(e) => {
                    if (e.key === "Backspace" && searchValue === "") {
                        popNavigation();
                    }
                }}
            />
        </SearchWrapper>
    );
}

export default Search;
